import { apiClient, type ApiResponse } from './api';
import type { User } from '@/store/userStore';
import { getDeviceIdentity } from '@/utils/appInfo';

/**
 * 发送短信验证码接口响应数据
 */
export type SendSmsResponse = ApiResponse<null>;

/**
 * 发送短信验证码
 * @param {string} telephone - 手机号码
 * @returns {Promise<SendSmsResponse>}
 */
export const sendSms = (telephone: string): Promise<SendSmsResponse> => {
  return apiClient.post<SendSmsResponse>('/api/pc/sms/send', { telephone }, { skipAuth: true });
};

/**
 * 登录接口响应数据中的data部分
 */
export type LoginData = {
  allowRebind: boolean;
  avatar: string | null;
  group: string;
  id: number;
  nickname: string | null;
  notification: Record<string, unknown>;
  status: string;
  summary: {
    sms: {
      imei: string;
      phone: string;
    };
  };
  token: string;
  vipEndedAt: string | null;
};

export const mapAuthUser = (authData: LoginData, fallbackPhone = ''): User => {
  return {
    id: authData.id,
    nickname: authData.nickname ?? null,
    group: authData.group,
    phone: authData.summary?.sms?.phone || fallbackPhone,
    imei: authData.summary?.sms?.imei ?? null,
    status: authData.status,
    allowRebind: authData.allowRebind,
    avatar: authData.avatar ?? null,
    vipEndedAt: authData.vipEndedAt ?? null,
    notification: authData.notification ?? {},
    summary: authData.summary
      ? {
          sms: {
            imei: authData.summary.sms?.imei ?? null,
            phone: authData.summary.sms?.phone ?? null,
          },
        }
      : undefined,
  };
};

/**
 * 登录接口响应数据
 */
export type LoginResponse = ApiResponse<LoginData>;

/**
 * 我的信息接口响应数据
 */
export type MyInfoResponse = ApiResponse<LoginData>;

/**
 * 短信登录
 * @param {string} telephone - 用户号码
 * @param {string} code - 短信验证码
 * @returns {Promise<LoginResponse>}
 */
export const smsLogin = async (telephone: string, code: string): Promise<LoginResponse> => {
  const deviceIdentity = await getDeviceIdentity();

  return apiClient.post<LoginResponse>(
    '/api/pc/sms/login',
    {
      telephone,
      code,
      deviceIdentity,
    },
    {
      skipAuth: true,
    },
  );
};

/**
 * 刷新当前设备的用户信息和 token
 */
export const getMyInfo = async (): Promise<MyInfoResponse> => {
  const deviceIdentity = await getDeviceIdentity();

  return apiClient.get<MyInfoResponse>('/api/pc/sms/my_info', { deviceIdentity });
};
